'use client';

import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { memo } from 'react';

import type { LayerBreadcrumbItem, LayerBreadcrumbProps } from './Layer.types';

/**
 * Individual breadcrumb item for layer navigation.
 * Uses onClick instead of href for in-layer content transitions.
 * Minimum 44px touch target for mobile accessibility.
 */
function LayerBreadcrumbItemComponent({
  label,
  icon,
  onClick,
  isCurrent = false,
}: LayerBreadcrumbItem) {
  const baseClasses =
    'inline-flex items-center gap-1.5 text-body-sm min-h-11 px-1';

  if (isCurrent || !onClick) {
    return (
      <span
        className={`${baseClasses} text-neutral-400`.trim()}
        aria-current={isCurrent ? 'page' : undefined}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        <span className="truncate">{label}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClasses} text-primary-300 transition-colors duration-fast hover:text-primary-400 active:text-primary-400 focus:outline-none focus-visible:ring-state-focus rounded`.trim()}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="truncate">{label}</span>
    </button>
  );
}

/**
 * Separator between breadcrumb items.
 */
function LayerBreadcrumbSeparator() {
  return (
    <span
      className="shrink-0 flex items-center min-h-11"
      role="presentation"
      aria-hidden="true"
    >
      <ChevronRightIcon className="h-4 w-4 text-neutral-600" />
    </span>
  );
}

/**
 * LayerBreadcrumb - Navigation breadcrumb for Layer 1 header.
 *
 * Extends the existing Breadcrumb design but uses onClick handlers instead
 * of href links for in-layer content transitions. Tapping items triggers
 * content transitions, not page navigation.
 *
 * Features:
 * - 44px minimum touch targets for mobile accessibility
 * - onClick-based navigation (triggers content transition)
 * - Tapping first item ('Plan') dismisses Layer 1 entirely
 * - Chevron separators between items
 * - Current item shown as non-clickable text
 *
 * @example
 * ```tsx
 * <LayerBreadcrumb
 *   items={[
 *     { label: 'Plan', onClick: () => closeLayer() },
 *     { label: 'Upper Body', onClick: () => navigateTo(1) },
 *     { label: 'Bench Press', isCurrent: true },
 *   ]}
 * />
 * ```
 */
export const LayerBreadcrumb = memo(function LayerBreadcrumb({
  items,
  className = '',
}: LayerBreadcrumbProps) {
  if (!items.length) {
    return null;
  }

  return (
    <nav
      aria-label="Layer navigation"
      data-testid="layer-breadcrumb"
      className={`flex items-center ${className}`.trim()}
    >
      <ol className="flex flex-wrap items-center gap-0.5">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li
              key={`${item.label}-${index}`}
              className="inline-flex items-center"
            >
              <LayerBreadcrumbItemComponent
                label={item.label}
                icon={item.icon}
                onClick={item.onClick}
                isCurrent={item.isCurrent ?? isLast}
              />
              {!isLast && <LayerBreadcrumbSeparator />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
});

export default LayerBreadcrumb;
